import * as React from "react";
import { flexRender, type Table as TanstackTable } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import { DataTableSkeletonRows } from "./data-table-skeleton";
import "./types";

export interface DataTableProps<TData> {
  table: TanstackTable<TData>;
  isLoading?: boolean;
  emptyMessage?: string;
  emptyAction?: React.ReactNode;
  selectedRowId?: string | null;
  hoveredRowId?: string | null;
  onRowClick?: (row: TData) => void;
  onRowHoverChange?: (row: TData | null) => void;
  /** Row id whose expanded content is shown directly beneath it. */
  expandedRowId?: string | null;
  /** Renders the expanded content for a row; used when its id matches expandedRowId. */
  renderExpanded?: (row: TData) => React.ReactNode;
  footer?: React.ReactNode;
  /** Tailwind max-height class for the scroll container. Omit for no cap. */
  maxHeightClassName?: string;
  className?: string;
}

export function DataTable<TData>({
  table,
  isLoading = false,
  emptyMessage = "Nothing here yet.",
  emptyAction,
  selectedRowId,
  hoveredRowId,
  onRowClick,
  onRowHoverChange,
  expandedRowId,
  renderExpanded,
  footer,
  maxHeightClassName,
  className,
}: DataTableProps<TData>) {
  const columnCount = table.getAllLeafColumns().length;
  const rows = table.getRowModel().rows;

  return (
    <div className={cn("relative w-full overflow-auto", maxHeightClassName, className)}>
      <table
        aria-busy={isLoading || undefined}
        className="w-full min-w-max border-collapse text-[13px] tabular-nums"
      >
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => {
                const sorted = h.column.getIsSorted();
                const align = h.column.columnDef.meta?.align;
                return (
                  <th
                    key={h.id}
                    scope="col"
                    aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : undefined}
                    className={cn(
                      "sticky top-0 z-10 h-[30px] whitespace-nowrap border-b border-foreground bg-background px-2.5 text-left align-middle font-medium text-muted-foreground",
                      align === "right" && "text-right"
                    )}
                  >
                    {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {isLoading ? (
            <DataTableSkeletonRows columnCount={columnCount} />
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={columnCount} className="h-[72px] px-2.5 text-muted-foreground">
                <span>{emptyMessage}</span>
                {emptyAction && <span className="ml-3">{emptyAction}</span>}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const isSelected = selectedRowId != null && row.id === selectedRowId;
              const isHovered = hoveredRowId != null && row.id === hoveredRowId;
              const isExpanded = renderExpanded != null && expandedRowId != null && row.id === expandedRowId;
              return (
                <React.Fragment key={row.id}>
                <tr
                  id={`row-${row.id}`}
                  data-state={isSelected ? "selected" : undefined}
                  className={cn(
                    "group border-b border-border transition-colors last:border-b-0 hover:bg-accent focus-within:bg-accent",
                    isHovered && !isSelected && "bg-accent",
                    isSelected && "bg-brand-soft hover:bg-brand-soft [&>td:first-child]:border-l-[3px] [&>td:first-child]:border-l-brand",
                    isExpanded && "border-b-0",
                    onRowClick && "cursor-pointer"
                  )}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  onMouseEnter={onRowHoverChange ? () => onRowHoverChange(row.original) : undefined}
                  onMouseLeave={onRowHoverChange ? () => onRowHoverChange(null) : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cn(
                        "h-8 whitespace-nowrap px-2.5 align-middle",
                        cell.column.columnDef.meta?.align === "right" && "text-right"
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
                {isExpanded && (
                  <tr
                    data-expanded-for={row.id}
                    className={cn(
                      "border-b border-border last:border-b-0",
                      isSelected && "[&>td]:border-l-[3px] [&>td]:border-l-brand"
                    )}
                  >
                    <td colSpan={columnCount} className="max-w-0 whitespace-normal p-0 align-top">
                      {renderExpanded(row.original)}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
        {footer && (
          <tfoot>
            <tr>
              <td colSpan={columnCount} className="h-[34px] px-2.5 text-[12.5px] text-muted-foreground">
                {footer}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
